import React, { useState } from 'react';
import { Upload, Form, Collapse, Space, Typography, Alert } from 'antd';
import { InboxOutlined, InfoCircleOutlined } from '@ant-design/icons';

const { Dragger } = Upload;
const { Title, Text } = Typography;

/* eslint-disable react/prop-types */
const UploadCSVStep = ({ onUpload, loading, csvFile }) => {
  const [fileList, setFileList] = useState([]);

  const uploadProps = {
    name: 'file',
    multiple: false,
    accept: '.csv',
    fileList: fileList,
    beforeUpload: (file) => {
      const isCSV = file.type === 'text/csv' || file.name.endsWith('.csv');
      if (!isCSV) {
        return Upload.LIST_IGNORE;
      }
      return false; // Prevent auto upload
    },
    onChange: (info) => {
      const { file, fileList: newFileList } = info;

      // Update fileList to show the uploaded file
      setFileList(newFileList);

      // When file is selected, immediately pass it to parent
      // Since beforeUpload returns false, originFileObj should exist
      if (file && file.originFileObj) {
        onUpload(file.originFileObj);
      } else if (file && !file.originFileObj && file.status !== 'removed') {
        // Fallback: sometimes the file object itself is what we need
        onUpload(file);
      }
    },
    maxCount: 1,
    showUploadList: true,
  };

  return (
    <div className="upload-csv-step">
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={4}>Upload Invoice CSV File</Title>
          <Text type="secondary">
            Please upload a CSV file containing invoice data. The file should include the following columns:
          </Text>
        </div>

        <Collapse
          size="small"
          items={[
            {
              key: '1',
              label: (
                <span>
                  <InfoCircleOutlined style={{ marginRight: 8, color: '#1890ff' }} />
                  Required CSV Columns (click to expand)
                </span>
              ),
              children: (
                <ul style={{ marginBottom: 0, paddingLeft: 20, columns: 2 }}>
                  <li><strong>CompanyKey</strong> (number, required)</li>
                  <li><strong>VendorLocationKey</strong> (number, required)</li>
                  <li><strong>BankCode</strong> (text, required)</li>
                  <li><strong>DueDate</strong> (date, required)</li>
                  <li><strong>InvoiceDate</strong> (date, required)</li>
                  <li><strong>PostedDate</strong> (date, optional)</li>
                  <li><strong>Reference</strong> (text, optional)</li>
                  <li><strong>OnlineBoardApproval</strong> (true/false, optional)</li>
                  <li><strong>InvoiceItemList</strong> (text, optional)</li>
                  <li><strong>ExpenseCode</strong> (text, required)</li>
                  <li><strong>InvoiceNumber</strong> (text, required)</li>
                  <li><strong>Memo</strong> (text, optional)</li>
                  <li><strong>Department</strong> (text, required)</li>
                  <li><strong>Amount</strong> (number, required)</li>
                  <li><strong>Cost</strong> (number, required)</li>
                  <li><strong>Quantity</strong> (number, required)</li>
                  <li><strong>UnitKey</strong> (number, optional)</li>
                  <li><strong>CompanyInventoryLinkKey</strong> (number, optional)</li>
                  <li><strong>ResidentKey</strong> (number, optional)</li>
                  <li><strong>FileName</strong> (text, optional)</li>
                  <li><strong>ImageBase64</strong> (text, optional)</li>
                </ul>
              ),
            },
          ]}
        />

        <Form layout="vertical">
          <Form.Item label="CSV File" required>
            <Dragger {...uploadProps} disabled={loading} data-testid="vmsinvoice-csv-dragger">
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                Click or drag CSV file to this area to upload
              </p>
              <p className="ant-upload-hint">
                Support for a single CSV file upload. File will be validated before processing.
              </p>
            </Dragger>
          </Form.Item>
        </Form>

        {csvFile && (
          <Alert
            message="File Uploaded"
            description={`Selected file: ${csvFile.name}`}
            type="success"
            showIcon
          />
        )}
      </Space>
    </div>
  );
};

export default UploadCSVStep;
